import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { decryptHubspotIdentifyPayloadJson } from "../../utils/hubspot-identify-payload-crypto";
import { getHubspotIdentifyPayloadSecret } from "../../utils/vite-env";
import {
    ensureHubSpotTrackingLoaded,
    getHubspotPortalId,
    hasHubSpotVisitorCookie,
    queueHubSpotIdentifyAndPageView,
} from "../../utils/hubspotTracking";

declare global {
    interface Window {
        /** HubSpot tracking queue (loaded via GTM); commands are pushed before the async script runs. */
        _hsq?: unknown[];
    }
}

const PARAM = "user_id";

/**
 * When the URL contains ?user_id=<hubspotCrmContactId>, resolves the contact email via the API
 * and queues HubSpot identify + trackPageView. Requires HubSpot tracking (GTM in prod, or
 * `VITE_HUBSPOT_PORTAL_ID` for local dev when GTM is disabled).
 * Internal team visits still create HubSpot page views when cookies allow; outreach-tool
 * filters them from the sales activity report via `SALES_ACTIVITY_EXCLUDED_CONTACT_IDS`.
 * Server timeline notes and Slack fire only for external contacts.
 */
export const HubSpotIdentifyFromUrl = () => {
    const [searchParams] = useSearchParams();
    const contactId = searchParams.get(PARAM)?.trim() ?? null;
    const lastResolvedContactId = useRef<string | null>(null);

    useEffect(() => {
        if (!contactId) {
            return;
        }

        if (lastResolvedContactId.current === contactId) {
            return;
        }

        let cancelled = false;

        const run = async () => {
            const res = await fetch(`/api/vertriebsportal/hubspot-contact-email?id=${encodeURIComponent(contactId)}`, {
                credentials: "same-origin",
            });

            if (cancelled || !res.ok) {
                return;
            }

            const data = (await res.json()) as {
                email?: string;
                v?: number;
                d?: string;
            };

            const payloadSecret = getHubspotIdentifyPayloadSecret();
            let email = "";

            if (typeof data.email === "string") {
                email = data.email.trim();
            } else if (payloadSecret && data.v === 1 && typeof data.d === "string" && data.d.length > 0) {
                try {
                    const decrypted = await decryptHubspotIdentifyPayloadJson(payloadSecret, data.d);
                    email = decrypted.email.trim();
                } catch {
                    return;
                }
            }

            if (!email) {
                return;
            }

            await ensureHubSpotTrackingLoaded();
            if (!getHubspotPortalId() && process.env.NODE_ENV === "development") {
                console.warn(
                    "[HubSpotIdentifyFromUrl] HubSpot tracking script not configured. Set VITE_HUBSPOT_PORTAL_ID in packages/website/.env for local page-view tests (GTM is off)."
                );
            }

            queueHubSpotIdentifyAndPageView(email);
            if (process.env.NODE_ENV === "development") {
                console.log("[HubSpotIdentifyFromUrl] identify + trackPageView queued", {
                    contactId,
                    path: `${window.location.pathname}${window.location.search}`,
                    hubspotutk: hasHubSpotVisitorCookie(),
                });
                if (!hasHubSpotVisitorCookie()) {
                    console.warn(
                        "[HubSpotIdentifyFromUrl] Kein hubspotutk-Cookie — HubSpot-Cookie-Banner akzeptieren, sonst wird kein Page View gesendet."
                    );
                }
            }
            lastResolvedContactId.current = contactId;
        };

        void run();

        return () => {
            cancelled = true;
        };
    }, [contactId]);

    return null;
};
